<script setup>
import { computed } from 'vue'
import Icon from './ui/Icon.vue'
import Meter from './ui/Meter.vue'
import SectionTitle from './ui/SectionTitle.vue'
import { isHidden } from '../stores/sections'
import {
  accountBalance,
  allowance,
  balance,
  budgetHealth,
  fmtGlobal as fmt,
  goalStates,
  hideGlobal,
  isCurrentMonth,
  nextMonth,
  nextMonthPlan,
  plannedProgress,
  plannedSpending,
  savingsTotals,
  showMonth,
  totals,
} from '../stores'
import { state } from '../stores/state'
import { monthLabel } from '../lib/dates'
import { t } from '../stores/i18n'

/** Only worth saying when the goals are holding something back. */
const heldBack = computed(() => Math.abs(accountBalance.value - balance.value) >= 0.01)

const allowanceTone = computed(() =>
  allowance.value.todayLeft < 0 ? 'text-warn' : 'text-accent',
)

/** The headline is a figure unless there is no figure worth saying. */
const stillToPay = computed(() => {
  const { planned, due } = plannedSpending.value
  if (hideGlobal.value || due > 0) return fmt(due, { round: true })
  return planned ? t('stats.allPaid') : t('stats.nothingPlanned')
})

/** The card shows every budget at once, so it wears the worst one's colour. */
const BUDGET_TONE = {
  good: { text: 'text-accent', bar: 'var(--c-accent)', hover: 'hover:border-accent/40' },
  warn: { text: 'text-warn', bar: 'var(--c-warn)', hover: 'hover:border-warn/40' },
  danger: { text: 'text-danger', bar: 'var(--c-danger)', hover: 'hover:border-danger/40' },
}
/** The total's own band colours the card; the worst single one colours its count. */
const budgetLook = computed(() => BUDGET_TONE[budgetHealth.value.tone] ?? BUDGET_TONE.good)
const worstLook = computed(() => BUDGET_TONE[budgetHealth.value.worst] ?? BUDGET_TONE.good)

const nextLabel = computed(() => monthLabel(nextMonth.value))
// The card is narrow on a phone; the full name lives in the label instead.
const nextShort = computed(() => monthLabel(nextMonth.value, { short: true }))
// There is no month switch to nudge any more: move the window itself.
const jumpToNext = () => showMonth(nextMonth.value)
</script>

<template>
  <section class="min-w-0" :class="isHidden('stats') ? '' : 'w-full'" data-section="stats">
    <SectionTitle id="stats" :title="t('stats.title')" />
    <!-- Eight of them: two rows of four from `lg` up, two across below it. Eight
         divides evenly into both, so no row is ever left with a card stranded in
         it. Four across only from `lg`: a phone on its side is `md` wide, and
         there a card would be narrower than a six-figure balance at `text-2xl`. -->
    <!-- One to a row on a phone: two side by side squeezed every figure and
         wrapped every line under it. -->
    <div v-if="!isHidden('stats')" class="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4">
      <div class="card p-4">
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="wallet" :size="14" /> {{ t('stats.balance') }}
        </p>
        <p class="tnum mt-1.5 text-2xl leading-none font-bold">{{ fmt(balance) }}</p>
        <p class="tnum mt-2 text-[12px] text-faint">
          {{ t('stats.projected') }}
          <span :class="allowance.projectedEnd < 0 ? 'text-danger' : 'text-muted'">
            {{ fmt(allowance.projectedEnd) }}
          </span>
        </p>
        <!-- The headline is what you may spend; this is what the bank shows. The
             difference between them is what the goals are holding, so it is only
             worth a line when there is a difference to explain. -->
        <p v-if="heldBack" class="tnum mt-1 text-[12px] text-faint">
          {{ t('stats.inTheAccount') }}
          <span class="text-muted">{{ fmt(accountBalance) }}</span>
        </p>
      </div>

      <div class="card p-4">
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="spark" :size="14" /> {{ t('stats.allowance') }}
        </p>
        <p class="tnum mt-1.5 text-2xl leading-none font-bold" :class="allowanceTone">
          {{ fmt(allowance.perDay) }}
        </p>
        <p class="tnum mt-2 text-[12px] text-faint">
          {{ t('stats.daysLeft', { count: allowance.daysLeft }) }} ·
          <span :class="allowance.todayLeft < 0 ? 'text-warn' : ''">
            {{ t('stats.leftToday', { amount: fmt(allowance.todayLeft) }) }}
          </span>
        </p>
      </div>

      <div class="card p-4">
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="in" :size="14" /> {{ isCurrentMonth ? t('stats.thisMonth') : t('stats.thatMonth') }}
        </p>
        <p class="tnum mt-1.5 text-2xl leading-none font-bold text-accent">
          {{ fmt(totals.income, { round: true }) }}
        </p>
        <p class="tnum mt-2 text-[12px] text-faint">
          {{ t('stats.spent', { amount: fmt(totals.spent, { round: true }) }) }} ·
          <span :class="totals.net < 0 ? 'text-warn' : ''">
            {{ t('stats.net', { amount: fmt(totals.net, { round: true }) }) }}
          </span>
        </p>
      </div>

      <button
        class="card flex flex-col p-4 text-left transition hover:border-warn/40"
        :aria-label="t('stats.stillToPayLabel')"
        @click="state.ui.panel = 'plan'"
      >
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="clock" :size="14" /> {{ t('stats.stillToPay') }}
        </p>
        <p
          class="tnum mt-1.5 leading-none font-bold"
          :class="plannedSpending.due > 0 || hideGlobal ? 'text-2xl' : 'text-xl text-accent'"
        >
          {{ stillToPay }}
        </p>
        <p class="tnum mt-2 truncate text-[12px] text-faint">
          {{ t('stats.paidSoFar', { amount: fmt(plannedSpending.paid, { round: true }) }) }}
        </p>
        <!-- The gap above it is the meter's own; `mt-auto` only decides how far
             down the card it sits, and in the tallest card of a row that is none. -->
        <div class="mt-auto">
          <Meter
            v-if="plannedSpending.planned"
            :value="plannedProgress"
            color="var(--c-accent)"
            :label="t('stats.plannedMeter', { percent: Math.round(plannedProgress * 100) })"
          />
        </div>
      </button>

      <button
        class="card flex flex-col p-4 text-left transition"
        :class="budgetLook.hover"
        :aria-label="t('stats.budgetsLabel')"
        @click="state.ui.panel = 'budgets'"
      >
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="gauge" :size="14" /> {{ t('budgets.title') }}
        </p>
        <!-- The figure is the gap, not the spend: "how far over or under am I"
             is the question, and the word carries the direction so the colour is
             never the only thing saying it. -->
        <p
          class="tnum mt-1.5 flex items-baseline gap-1.5 leading-none font-bold"
          :class="budgetHealth.limits ? budgetLook.text : ''"
        >
          <template v-if="budgetHealth.limits && budgetHealth.variance === 0">
            <span class="text-xl">{{ t('stats.exactlyOnBudget') }}</span>
          </template>
          <template v-else-if="budgetHealth.limits">
            <span class="text-2xl">{{ fmt(Math.abs(budgetHealth.variance), { round: true }) }}</span>
            <span class="text-[15px] font-semibold">
              {{ budgetHealth.variance > 0 ? t('stats.over') : t('stats.under') }}
            </span>
          </template>
          <template v-else-if="budgetHealth.total">
            <span class="text-xl">{{ t('stats.budgetsSet', { count: budgetHealth.total }) }}</span>
          </template>
          <template v-else><span class="text-xl">{{ t('stats.noneYet') }}</span></template>
        </p>
        <p class="tnum mt-2 truncate text-[12px] text-faint">
          <template v-if="budgetHealth.limits">
            {{ t('common.xOfY', { x: fmt(budgetHealth.spent, { round: true }), y: fmt(budgetHealth.budgeted, { round: true }) }) }}
            <template v-if="budgetHealth.alarming">
              · <span :class="worstLook.text">{{ t('stats.offTrack', { count: budgetHealth.alarming }) }}</span>
            </template>
          </template>
          <template v-else-if="budgetHealth.total">{{ t('budgets.allOnTrackShort') }}</template>
          <template v-else>{{ t('stats.budgetsHint') }}</template>
        </p>
        <!-- The gap above it is the meter's own; `mt-auto` only decides how far
             down the card it sits, and in the tallest card of a row that is none. -->
        <div class="mt-auto">
          <Meter
            v-if="budgetHealth.limits"
            :value="budgetHealth.progress"
            :color="budgetLook.bar"
            :label="t('stats.budgetMeter', { percent: Math.round(budgetHealth.progress * 100) })"
          />
        </div>
      </button>

      <button
        class="card p-4 text-left transition hover:border-accent/40"
        :aria-label="t('stats.nextInLabel', { month: nextLabel })"
        @click="jumpToNext"
      >
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="calendar" :size="14" /> {{ t('stats.nextIn') }}
        </p>
        <p class="tnum mt-1.5 text-2xl leading-none font-bold text-accent">
          {{ fmt(nextMonthPlan.income, { round: true }) }}
        </p>
        <p class="tnum mt-2 truncate text-[12px] text-faint">
          {{ t('stats.plannedFor', { month: nextShort }) }}
        </p>
      </button>

      <button
        class="card p-4 text-left transition hover:border-warn/40"
        :aria-label="t('stats.nextOutLabel', { month: nextLabel })"
        @click="jumpToNext"
      >
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="calendar" :size="14" /> {{ t('stats.nextOut') }}
        </p>
        <p class="tnum mt-1.5 text-2xl leading-none font-bold">
          {{ fmt(nextMonthPlan.expense, { round: true }) }}
        </p>
        <p class="tnum mt-2 truncate text-[12px] text-faint">
          {{ t('stats.plannedItems', { count: nextMonthPlan.items }) }}
        </p>
      </button>

      <button
        class="card flex flex-col p-4 text-left transition hover:border-goal/40"
        @click="state.ui.panel = 'goals'"
      >
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon name="target" :size="14" /> {{ t('stats.saved') }}
        </p>
        <p class="tnum mt-1.5 text-2xl leading-none font-bold text-goal">
          {{ fmt(savingsTotals.saved, { round: true }) }}
        </p>
        <p class="tnum mt-2 text-[12px] text-faint">
          <template v-if="savingsTotals.target > 0">
            {{ t('stats.savedOf', { amount: fmt(savingsTotals.target, { round: true }) }) }} ·
            {{ t('stats.savedThisMonth', { amount: fmt(savingsTotals.thisMonth, { round: true }) }) }}
          </template>
          <template v-else>{{ t('stats.noGoals') }}</template>
        </p>
        <!-- The gap above it is the meter's own; `mt-auto` only decides how far
             down the card it sits, and in the tallest card of a row that is none. -->
        <div class="mt-auto">
          <Meter
            v-if="savingsTotals.target > 0"
            :value="savingsTotals.progress"
            color="var(--c-goal)"
            :label="t('stats.savedMeter', { percent: Math.round(savingsTotals.progress * 100) })"
          />
        </div>
      </button>
    </div>
  </section>
</template>
